import { useState, useEffect, useCallback } from 'react'
import { useTableColumns } from '@/hooks/useTableColumns'
import { useSortedRows } from '@/hooks/useSortedRows'
import type { SortAccessors } from '@/lib/tableSort'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import { ColumnCells, ResizableTh } from '@/components/table'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  ArrowLeft,
  Loader2,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  Database,
  Cpu,
  Activity,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { LisPreMappingTab } from '@/components/integrations/LisPreMappingTab'
import { LisMappingTab } from '@/components/integrations/LisMappingTab'
import { LisOutgoingMappingTab } from '@/components/integrations/LisOutgoingMappingTab'
import { LisMapErrorsTab } from '@/components/integrations/LisMapErrorsTab'
import { useQuery } from '@tanstack/react-query'
import {
  integrationsApi,
  type Integration,
  type InstrumentResultRow,
  type Equipment,
  type Feed,
  type IngestSummary,
  RESULTS_DESTINATION_LABEL,
} from '@/api/integrationsApi'
import { sourcesApi } from '@/api/sourcesApi'
import { useTenant } from '@/providers/TenantProvider'
import { IntegrationMappingsTab } from '@/components/integrations/IntegrationMappingsTab'
import { IntegrationForm } from '@/components/integrations/IntegrationForm'
import { cn } from '@/lib/utils'
import { LisReviewButton } from '@/components/lis-review/LisReviewButton'

const DEFAULT_DIN = 'TEST-A1234567890123'
const DEFAULT_PRODUCT = 'C01-AphPBSC'
const SCENARIOS = [
  'happy-path',
  'cv-exceeded',
  'uloq-exceeded',
  'quarantine',
  'not-found',
  'nc200-happy',
  'nc200-low-viability',
  'nc200-quarantine',
  'epic-happy',
  'epic-unresolvable',
]

// A short, variant-aware summary of a result row from its payload.
function resultSummary(row: InstrumentResultRow): string {
  const p = row.payload as Record<string, unknown>
  switch (row.resultType) {
    case 'ella':
      return `${p.analyte ?? '—'}: ${p.concentration ?? '—'} ${p.units ?? ''} (CV ${p.cvPercent ?? '—'}%)`
    case 'nc200':
      return `${p.sampleId ?? '—'}: viability ${p.viability ?? '—'}% · viable ${p.viableCellCount ?? '—'}`
    case 'epic':
      return `${p.orderType ?? '—'} · ${p.facility ?? '—'}`
    default:
      return ''
  }
}

function StatusBadge({ status }: { status: string }) {
  const ok = status === 'completed' || status === 'correlated'
  return (
    <Badge
      className={cn(
        'border text-xs',
        ok
          ? 'border-green-200 bg-green-100 text-green-800'
          : 'border-amber-200 bg-amber-100 text-amber-800',
      )}
    >
      {status}
    </Badge>
  )
}

// Pre-map comes BEFORE the LIS mapping, and both before the rest: a pre-map
// produces the named value a mapping consumes, so that is the order the work
// happens in. `hl7-config` is the tab's former name — aliased below rather than
// dropped, so links shared before the rename still land correctly, the same way
// `?tab=simulation` is handled on IntegrationsPage.
const TAB_VALUES = [
  'pre-mapping',
  'mapping',
  'mappings',
  'results',
  'instrument',
  'health',
  'map-errors',
  'edit',
]

// `mappings` / `results` / `instrument` all read an `InstrumentResultType`, which
// a laboratory source does not have — they can do nothing on a LIS integration,
// so they are hidden there rather than left inert.
const INSTRUMENT_ONLY = new Set(['mappings', 'results', 'instrument'])

// Former names, kept so links shared before each rename still land. `hl7-config`
// and `lis-mapping` became Mapping; `schemas` and `pre-map` became Pre-Mapping.
const TAB_ALIASES: Record<string, string> = {
  'hl7-config': 'mapping',
  'lis-mapping': 'mapping',
  // The source configuration's earlier names.
  schemas: 'pre-mapping',
  'pre-map': 'pre-mapping',
}

// Per-user column layout for the instrument-results table. Type and Key are
// locked: together they are what identifies a result row. See
// src/components/table/index.ts for the pattern.
const INTEGRATION_RESULTS_TABLE_ID = 'integration-results'

const INTEGRATION_RESULTS_COLUMNS: TableColumnDef[] = [
  { id: 'resultType', header: 'Type', locked: true, sortable: true },
  { id: 'rowKey', header: 'Key', locked: true, sortable: true },
  { id: 'detail', header: 'Detail', sortable: true },
  { id: 'status', header: 'Status', sortable: true },
  { id: 'flags', header: 'Flags', sortable: true },
  { id: 'version', header: 'Ver', sortable: true },
]

// Header sorting (z8nrz7e4h6). Unsorted, results keep the API's order.
const INTEGRATION_RESULTS_SORT: SortAccessors<InstrumentResultRow> = {
  resultType: r => r.resultType,
  rowKey: r => r.rowKey,
  detail: r => r.reason ?? resultSummary(r),
  status: r => r.status,
  flags: r => r.flags.join(', '),
  version: r => r.version,
}

export function IntegrationDetailPage() {
  const cols = useTableColumns(INTEGRATION_RESULTS_TABLE_ID, INTEGRATION_RESULTS_COLUMNS)
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { selectedTenantId } = useTenant()

  // Tabs are URL-driven (?tab=) so they can be deep-linked (e.g. "Map to field").
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get('tab')
  const aliased = tabParam ? (TAB_ALIASES[tabParam] ?? tabParam) : null
  const requestedTab = aliased && TAB_VALUES.includes(aliased) ? aliased : null
  const setTab = (v: string) => {
    const next = new URLSearchParams(searchParams)
    next.set('tab', v)
    setSearchParams(next, { replace: true })
  }

  const [integration, setIntegration] = useState<Integration | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [mappingDirection, setMappingDirection] = useState<'incoming' | 'outgoing'>('incoming')

  const [din, setDin] = useState(DEFAULT_DIN)
  const [productCode, setProductCode] = useState(DEFAULT_PRODUCT)
  const [scenario, setScenario] = useState<string>('')
  const [fetching, setFetching] = useState(false)
  const [summary, setSummary] = useState<IngestSummary | null>(null)

  const [results, setResults] = useState<InstrumentResultRow[]>([])
  const sortedResults = useSortedRows(results, cols.sort, INTEGRATION_RESULTS_SORT)
  const [equipment, setEquipment] = useState<Equipment[]>([])
  const [feeds, setFeeds] = useState<Feed[]>([])

  const { data: sources = [] } = useQuery({ queryKey: ['sources'], queryFn: sourcesApi.list })
  const source = sources.find((s) => s.id === integration?.sourceId)
  const sourceName = source?.name
  /**
   * HL7 result mapping belongs to a laboratory connection, so the tab only
   * exists on one. An instrument or printer integration has no test codes and
   * no observations, and offering the tab there would invite someone to
   * configure a mapping that can never fire.
   */
  const isLis = source?.category === 'lis'

  /**
   * Which tab is showing.
   *
   * Derived HERE and not beside `searchParams`, because it depends on the
   * source's category, which is only known once the integration has loaded. A
   * LIS integration hides the instrument tabs, so `?tab=mappings` on one would
   * otherwise select a trigger that is not rendered and show an empty page.
   */
  const defaultTab = isLis ? 'mapping' : 'mappings'
  const tab =
    requestedTab && !(isLis && INSTRUMENT_ONLY.has(requestedTab))
      ? requestedTab
      : !requestedTab || isLis
        ? defaultTab
        : requestedTab

  const refreshResults = useCallback(() => {
    if (!selectedTenantId || !id) return
    integrationsApi.listResults(selectedTenantId, id).then(setResults).catch(() => {})
  }, [selectedTenantId, id])

  // Fetch (or re-fetch, e.g. after an edit) the integration itself — kept separate
  // from `loading` so a save doesn't blank the whole page.
  const loadIntegration = useCallback(async () => {
    if (!selectedTenantId || !id) return
    try {
      const i = await integrationsApi.get(selectedTenantId, id)
      setIntegration(i)
      const cfgScenario = (i.config?.scenario as string) ?? ''
      if (cfgScenario) setScenario(cfgScenario)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load integration.')
    }
  }, [selectedTenantId, id])

  useEffect(() => {
    if (!selectedTenantId || !id) return
    setLoading(true)
    setError(null)
    loadIntegration().finally(() => setLoading(false))
    refreshResults()
  }, [selectedTenantId, id, loadIntegration, refreshResults])

  function loadEquipment() {
    if (!selectedTenantId || !id) return
    integrationsApi
      .getEquipment(selectedTenantId, id)
      .then((r) => setEquipment(r.equipment))
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load equipment.'))
  }

  function loadFeeds() {
    if (!selectedTenantId || !id) return
    integrationsApi
      .getFeeds(selectedTenantId, id)
      .then((r) => setFeeds(r.feeds))
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load feeds.'))
  }

  // Lazy-load the active tab's data (also covers deep-links via ?tab=).
  useEffect(() => {
    if (!selectedTenantId || !id) return
    if (tab === 'instrument') loadEquipment()
    else if (tab === 'health') loadFeeds()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, selectedTenantId, id])

  async function handleFetch() {
    if (!selectedTenantId || !id) return
    setFetching(true)
    setError(null)
    setSummary(null)
    try {
      const s = await integrationsApi.fetchResults(selectedTenantId, id, {
        din,
        productCode,
        scenario: scenario || undefined,
      })
      setSummary(s)
      refreshResults()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fetch failed.')
    } finally {
      setFetching(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const latestCount = results.filter((r) => r.isLatest).length

  return (
    <div className="mx-auto max-w-screen-2xl px-6 lg:px-8 py-8">
      <div className="mb-6 flex items-center gap-3">
        <button
          onClick={() => navigate('/integrations')}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Integrations
        </button>
        <span className="text-muted-foreground">/</span>
        <span className="text-sm font-medium">{integration?.name ?? 'Integration'}</span>
      </div>

      {/* Header card */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4 rounded-lg border p-5">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-bold">{integration?.name}</h1>
            {isLis && <LisReviewButton />}
            {integration && (
              <Badge
                className={cn(
                  'border text-xs',
                  integration.viaMiddleware
                    ? 'border-violet-200 bg-violet-100 text-violet-800'
                    : 'border-sky-200 bg-sky-100 text-sky-800',
                )}
              >
                {integration.viaMiddleware ? 'Via middleware' : 'Direct'}
              </Badge>
            )}
            <Badge
              className={cn(
                'border text-xs',
                integration?.status === 'active'
                  ? 'border-green-200 bg-green-100 text-green-800'
                  : 'border-gray-200 bg-gray-100 text-gray-600',
              )}
            >
              {integration?.status}
            </Badge>
          </div>
          <dl className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted-foreground">
            {sourceName && (
              <div className="flex items-center gap-1.5">
                <span className="text-xs uppercase tracking-wide text-muted-foreground/70">
                  Source
                </span>
                <span className="text-foreground">{sourceName}</span>
              </div>
            )}
            <div className="flex items-center gap-1.5">
              <span className="text-xs uppercase tracking-wide text-muted-foreground/70">URL</span>
              <span className="truncate">{(integration?.config?.baseApiUrl as string) ?? '—'}</span>
            </div>
            {isLis && integration && (
              <div className="flex items-center gap-1.5">
                <span className="text-xs uppercase tracking-wide text-muted-foreground/70">
                  Results go to
                </span>
                <span className="text-foreground">
                  {RESULTS_DESTINATION_LABEL[integration.resultsDestination ?? 'beaker']}
                </span>
              </div>
            )}
          </dl>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-2xl font-bold tabular-nums">{latestCount}</div>
            <div className="text-xs text-muted-foreground">latest results</div>
          </div>
          <Button variant="outline" onClick={() => setTab('edit')}>
            Edit
          </Button>
        </div>
      </div>

      {error && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4">
          {isLis && <TabsTrigger value="pre-mapping">Pre-Mapping</TabsTrigger>}
          {isLis && <TabsTrigger value="mapping">Mapping</TabsTrigger>}
          {!isLis && <TabsTrigger value="mappings">Mappings</TabsTrigger>}
          {!isLis && <TabsTrigger value="results">Results</TabsTrigger>}
          {!isLis && <TabsTrigger value="instrument">Instrument</TabsTrigger>}
          <TabsTrigger value="health">Health</TabsTrigger>
          {isLis && <TabsTrigger value="map-errors">Map Error Logs</TabsTrigger>}
          <TabsTrigger value="edit">Edit</TabsTrigger>
        </TabsList>

        {/* Results (with the Fetch panel) */}
        <TabsContent value="results">
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="mb-3 flex items-center gap-2">
                <Database className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-sm font-semibold">Fetch by correlation key</h3>
              </div>
              <div className="grid gap-4 md:grid-cols-4">
                <div className="grid gap-1.5">
                  <Label htmlFor="din">DIN</Label>
                  <Input id="din" value={din} onChange={(e) => setDin(e.target.value)} />
                  <p className="text-xs text-muted-foreground">
                    ISBT-128 donation number CGT sends to the lab.
                  </p>
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="productCode">Product code</Label>
                  <Input
                    id="productCode"
                    value={productCode}
                    onChange={(e) => setProductCode(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">Classifies the cellular product.</p>
                </div>
                <div className="grid gap-1.5">
                  <Label>Scenario</Label>
                  <Select value={scenario || undefined} onValueChange={setScenario}>
                    <SelectTrigger>
                      <SelectValue placeholder="Default" />
                    </SelectTrigger>
                    <SelectContent>
                      {SCENARIOS.map((s) => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">Mock only.</p>
                </div>
                <div className="flex items-start">
                  <Button
                    onClick={handleFetch}
                    disabled={fetching || !din || !productCode}
                    className="mt-6 w-full"
                  >
                    {fetching ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="mr-2 h-4 w-4" />
                    )}
                    Fetch results
                  </Button>
                </div>
              </div>
              {summary && (
                <div className="mt-3 flex items-center gap-2 text-sm text-green-700">
                  <CheckCircle2 className="h-4 w-4" />
                  Persisted {summary.persisted} result(s)
                  {summary.quarantined > 0 && `, ${summary.quarantined} quarantined`}
                  {summary.received === 0 && ' — no results found for this correlation key'}
                  {summary.procedureId && ` · correlated to procedure ${summary.procedureId}`}
                </div>
              )}
            </CardContent>
          </Card>

          {results.length === 0 ? (
            <div className="rounded-lg border border-dashed py-16 text-center text-sm text-muted-foreground">
              No results yet. Use <strong>Fetch results</strong> above to pull from the
              integration-service.
            </div>
          ) : (
            <>
            <div className="overflow-hidden rounded-lg border">
              <table className="w-full text-sm">
                <thead className="border-b bg-table-header">
                  <tr>
                    {cols.visible.map(({ def }) => (
                      <ResizableTh
                        key={def.id}
                        columnId={def.id}
                        columns={cols}
                        label={def.header}
                        className="px-4 py-3 text-left font-medium text-table-header-foreground"
                      >
                        {def.header}
                      </ResizableTh>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedResults.map((r, i) => (
                    <tr
                      key={r.id}
                      className={cn(
                        'border-b last:border-0',
                        r.status === 'quarantined'
                          ? 'bg-amber-50'
                          : i % 2 === 0
                            ? 'bg-background'
                            : 'bg-muted/20',
                      )}
                    >
                      <ColumnCells
                        columns={cols}
                        cells={{
                          resultType: <td className="px-4 py-3 text-xs font-medium uppercase">{r.resultType}</td>,
                          rowKey: <td className="px-4 py-3 text-muted-foreground">{r.rowKey}</td>,
                          detail: <td className="px-4 py-3">{r.reason ?? resultSummary(r)}</td>,
                          status: (
                            <td className="px-4 py-3">
                              <StatusBadge status={r.status} />
                            </td>
                          ),
                          flags: (
                            <td className="px-4 py-3">
                              <div className="flex flex-wrap gap-1">
                                {r.flags.map((f) => (
                                  <span
                                    key={f}
                                    className="rounded border border-red-200 bg-red-100 px-1.5 py-0.5 text-[10px] text-red-700"
                                  >
                                    {f}
                                  </span>
                                ))}
                              </div>
                            </td>
                          ),
                          version: (
                            <td className="px-4 py-3">
                              v{r.version}
                              {r.isLatest && <span className="ml-1 text-[10px] text-green-700">latest</span>}
                            </td>
                          ),
                        }}
                      />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </TabsContent>

        {/* Mappings (instrument value → form field, scoped to this integration) */}
        <TabsContent value="mappings">
          {id && <IntegrationMappingsTab integrationId={id} tenantId={selectedTenantId} />}
        </TabsContent>

        {/* Instrument (equipment detail) */}
        <TabsContent value="instrument">
          {equipment.length === 0 ? (
            <div className="rounded-lg border border-dashed py-16 text-center text-sm text-muted-foreground">
              <Cpu className="mx-auto mb-3 h-7 w-7 text-muted-foreground/40" />
              No device identified yet. Use <strong>Fetch results</strong> first — the instrument's
              equipment is derived from the results it produced.
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {equipment.map((e) => (
                <Card key={e.id}>
                  <CardContent className="p-4">
                    <div className="mb-2 flex items-center gap-2">
                      <Cpu className="h-4 w-4 text-muted-foreground" />
                      <div className="font-medium">{e.model}</div>
                    </div>
                    <div className="mb-2 text-xs text-muted-foreground">
                      {e.id} · {e.type}
                    </div>
                    <dl className="space-y-1 text-sm">
                      <Row label="Manufacturer" value={e.manufacturer} />
                      <Row label="Serial / Lot" value={e.serialOrLot} />
                      <Row label="Config" value={e.configVersion} />
                      <Row label="Effective" value={e.effectiveDate} />
                    </dl>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Health (feeds) */}
        <TabsContent value="health">
          {feeds.length === 0 ? (
            <div className="rounded-lg border border-dashed py-16 text-center text-sm text-muted-foreground">
              <Activity className="mx-auto mb-3 h-7 w-7 text-muted-foreground/40" />
              No feed for this instrument yet. Use <strong>Fetch results</strong> first — the feed is
              matched to the device this integration produced results from.
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {feeds.map((f) => (
                <Card key={f.feedId}>
                  <CardContent className="p-4">
                    <div className="mb-2 flex items-center justify-between">
                      <div className="font-medium">{f.displayName}</div>
                      <Badge
                        className={cn(
                          'border text-xs',
                          f.connectionState === 'connected'
                            ? 'border-green-200 bg-green-100 text-green-800'
                            : f.connectionState === 'degraded'
                              ? 'border-amber-200 bg-amber-100 text-amber-800'
                              : 'border-red-200 bg-red-100 text-red-700',
                        )}
                      >
                        {f.connectionState}
                      </Badge>
                    </div>
                    <dl className="space-y-1 text-sm">
                      <Row label="Transport" value={f.transport} />
                      <Row label="Queue depth" value={String(f.queueDepth)} />
                      <Row label="Errors" value={String(f.errorCount)} />
                      <Row label="Quarantined" value={String(f.quarantineCount)} />
                    </dl>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Edit (the integration connection form) */}
        <TabsContent value="pre-mapping">
          <LisPreMappingTab />
        </TabsContent>

        <TabsContent value="mapping">
          {/* A segmented control, not nested Tabs: a second tabpanel would break
              the unscoped tabpanel locators in the e2e flows. */}
          <div className="mb-4 inline-flex rounded-md border p-0.5" role="group" aria-label="Mapping direction">
            {(['incoming', 'outgoing'] as const).map((dir) => (
              <button
                key={dir}
                type="button"
                aria-pressed={mappingDirection === dir}
                onClick={() => setMappingDirection(dir)}
                className={cn(
                  'rounded px-3 py-1 text-sm',
                  mappingDirection === dir
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {dir === 'incoming' ? 'Incoming Mapping' : 'Outgoing Mapping'}
              </button>
            ))}
          </div>
          {mappingDirection === 'incoming' ? (
            <LisMappingTab />
          ) : (
            id && (
              <LisOutgoingMappingTab
                integrationId={id}
                destination={integration?.resultsDestination ?? 'beaker'}
              />
            )
          )}
        </TabsContent>

        <TabsContent value="map-errors">
          <LisMapErrorsTab />
        </TabsContent>

        <TabsContent value="edit">
          {id && (
            <IntegrationForm
              integrationId={id}
              onSaved={() => {
                loadIntegration()
                setTab('results')
              }}
              onCancel={() => setTab('results')}
            />
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <dt className="text-muted-foreground">{label}</dt>
      <dd>{value}</dd>
    </div>
  )
}
